import { useEffect, useState } from 'react';
import type { Resource, ResourceKind } from '@/types';
import { Database, Layers, Eye, Plus, Edit3, Trash2, Lock } from 'lucide-react';
import { useResources } from '@/store/resource';
import { useLayout } from '@/contexts/layout-context';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/loader';
import { TabBar } from '@/components/ui/TabBar';
import { PageLayout } from '@/components/page-layout';
import {
  ComposedTable,
  StatusCell,
  BadgeCell,
} from '@/components/table';

export const ResourcesPage = () => {
  const { setBreadcrumbs } = useLayout();

  // Store actions and state
  const {
    resources,
    resourceKinds,
    isLoading,
    listResources,
    listResourceKinds,
    createResourceKind,
    updateResourceKind,
    deleteResourceKind,
  } = useResources();

  // Tabs state
  const [activeTab, setActiveTab] = useState<string>('resources');

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedKind, setSelectedKind] = useState<ResourceKind | null>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    parent_id: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ name: 'Resources' }]);
    Promise.all([listResources(), listResourceKinds()]).catch((e) =>
      console.error('Failed to load resources page data', e)
    );
  }, [listResources, listResourceKinds, setBreadcrumbs]);

  // Tab definitions
  const tabs = [
    { id: 'resources', label: 'Resources', icon: Database },
    { id: 'kinds', label: 'Resource Kinds', icon: Layers },
  ];

  // --- Resource Kind Create / Edit actions ---
  const handleOpenCreateModal = () => {
    setFormData({ id: '', name: '', description: '', parent_id: '' });
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (kind: ResourceKind) => {
    setSelectedKind(kind);
    setFormData({
      id: kind.id,
      name: kind.name,
      description: kind.description || '',
      parent_id: kind.parent_id || '',
    });
    setIsEditModalOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await createResourceKind({
        id: formData.id,
        name: formData.name,
        description: formData.description || undefined,
        parent_id: formData.parent_id || undefined,
      });
      setIsCreateModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedKind) return;
    setIsSaving(true);
    try {
      await updateResourceKind(selectedKind.id, {
        name: formData.name,
        description: formData.description || undefined,
        parent_id: formData.parent_id || undefined,
      });
      setIsEditModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteKind = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this resource kind?')) return;
    try {
      await deleteResourceKind(id);
    } catch (err) {
      console.error(err);
    }
  };

  // --- Resources Columns ---
  const resourceColumns = [
    {
      id: 'uri',
      header: 'Resource',
      accessorKey: 'uri',
      cell: ({ row }: any) => {
        const res = row.original as Resource;
        let displayName = res.uri;
        let logo = null;
        let displayKind = null;

        if (res.metadata?.tenant_name) {
          displayName = res.metadata.tenant_name;
          displayKind = 'Tenant';
          logo = res.metadata.tenant_logo ? (
            <img src={res.metadata.tenant_logo} alt="" className="w-6 h-6 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-[11px] font-bold text-[--primary] shrink-0">T</div>
          );
        } else if (res.metadata?.application_name) {
          displayName = res.metadata.application_name;
          displayKind = 'Application';
          logo = res.metadata.application_logo ? (
            <img src={res.metadata.application_logo} alt="" className="w-6 h-6 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded bg-purple-50 dark:bg-purple-500/10 flex items-center justify-center text-[11px] font-bold text-purple-600 shrink-0">A</div>
          );
        }

        return (
          <div className="flex items-center gap-3">
            {logo}
            <div className="flex flex-col min-w-0">
              <span
                className="text-[14px] font-bold text-slate-900 dark:text-white cursor-pointer hover:text-[--primary] truncate"
                onClick={() => setSelectedResource(res)}
              >
                {displayName}
              </span>
              <span className="text-[10px] text-slate-400 font-mono mt-0.5 truncate">{res.uri}</span>
              {displayKind && (
                <span className="text-[9px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-0.5">{displayKind}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'kind_id',
      header: 'Kind',
      accessorKey: 'kind_id',
      cell: ({ row }: any) => {
        const res = row.original as Resource;
        return <BadgeCell label={res.kind_id} variant="purple" />;
      },
    },
    {
      id: 'parent_id',
      header: 'Parent ID',
      accessorKey: 'parent_id',
      cell: ({ row }: any) => {
        const res = row.original as Resource;
        return res.parent_id ? (
          <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
            {res.parent_id}
          </span>
        ) : (
          <span className="text-xs text-slate-400 italic">None</span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }: any) => {
        const res = row.original as Resource;
        return <StatusCell status={res.status === 1 ? 'Active' : 'Inactive'} />;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: any) => (
        <button
          onClick={() => setSelectedResource(row.original as Resource)}
          className="p-1.5 text-slate-400 hover:text-[--primary] hover:bg-slate-50 dark:hover:bg-slate-900 rounded-md transition-colors"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  // --- Resource Kinds Columns ---
  const kindColumns = [
    {
      id: 'name',
      header: 'Kind Name',
      accessorKey: 'name',
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        return (
          <div className="flex flex-col">
            <span className="text-[14px] font-bold text-slate-900 dark:text-white">
              {kind.name}
            </span>
            <span className="text-xs text-slate-400 font-mono mt-0.5">{kind.id}</span>
          </div>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        return (
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {kind.description || 'No description'}
          </span>
        );
      },
    },
    {
      id: 'parent_id',
      header: 'Parent Kind',
      accessorKey: 'parent_id',
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        return kind.parent_id ? (
          <BadgeCell label={kind.parent_id} variant="default" />
        ) : (
          <span className="text-xs text-slate-400 italic">None</span>
        );
      },
    },
    {
      id: 'system',
      header: 'System Managed',
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        return kind.is_system_managed ? (
          <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded-md">
            <Lock className="w-3 h-3" /> System
          </span>
        ) : (
          <span className="text-xs text-slate-400">Custom</span>
        );
      },
    },
    {
      id: 'usage',
      header: 'In Use',
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        return kind.in_use ? (
          <span className="text-xs text-red-500 font-semibold bg-red-50 dark:bg-red-950/20 px-2 py-1 rounded-md">
            Yes
          </span>
        ) : (
          <span className="text-xs text-green-600 dark:text-green-400 font-semibold bg-green-50 dark:bg-green-950/20 px-2 py-1 rounded-md">
            No
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: any) => {
        const kind = row.original as ResourceKind;
        const isDisabled = kind.is_system_managed || kind.in_use;

        return (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => handleOpenEditModal(kind)}
              disabled={isDisabled}
              className="p-1.5 text-slate-400 hover:text-[--primary] hover:bg-slate-50 dark:hover:bg-slate-900 rounded-md transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
              title={isDisabled ? 'Cannot edit system-managed or in-use resource kinds' : 'Edit'}
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteKind(kind.id)}
              disabled={isDisabled}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
              title={isDisabled ? 'Cannot delete system-managed or in-use resource kinds' : 'Delete'}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ];

  // Filters (optional, placeholder matching other screens)
  const resourceFilters = [
    {
      type: 'radio' as const,
      key: 'status',
      label: 'Status',
      options: ['All', 'Active', 'Inactive'],
      defaultValue: 'All',
    },
  ];

  const resourceFilterFn = (row: Resource, values: any) => {
    const status = values['status'];
    if (status && status !== 'All') {
      const isActive = status === 'Active';
      if (isActive && row.status !== 1) return false;
      if (!isActive && row.status !== 2) return false;
    }
    return true;
  };

  const kindFilters = [
    {
      type: 'radio' as const,
      key: 'system',
      label: 'Type',
      options: ['All', 'System', 'Custom'],
      defaultValue: 'All',
    },
  ];

  const kindFilterFn = (row: ResourceKind, values: any) => {
    const system = values['system'];
    if (system && system !== 'All') {
      const isSystem = system === 'System';
      if (isSystem && !row.is_system_managed) return false;
      if (!isSystem && row.is_system_managed) return false;
    }
    return true;
  };

  if (isLoading && resources.length === 0) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <PageLayout title="Resources & Kinds" description="Manage enterprise resources and logical resource kinds.">
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {activeTab === 'resources' ? (
        <div className="space-y-4">
          <ComposedTable
            data={resources}
            columns={resourceColumns}
            filters={resourceFilters}
            filterFn={resourceFilterFn}
            searchPlaceholder="Search resources by URI..."
            emptyState={
              <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
                <Database className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No resources found</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Resources are registered dynamically by target services.
                </p>
              </div>
            }
            itemLabel="resources"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end mb-2">
            <Button onClick={handleOpenCreateModal} className="flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Resource Kind
            </Button>
          </div>

          <ComposedTable
            data={resourceKinds}
            columns={kindColumns}
            filters={kindFilters}
            filterFn={kindFilterFn}
            searchPlaceholder="Search resource kinds by name or ID..."
            emptyState={
              <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
                <Layers className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No resource kinds found</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Create a resource kind to classify enterprise resource objects.
                </p>
                <Button onClick={handleOpenCreateModal}>Create first kind</Button>
              </div>
            }
            itemLabel="resource kinds"
          />
        </div>
      )}

      {/* --- Detail View Modal for Resource --- */}
      {selectedResource && (
        <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <Database className="w-5 h-5 text-[--primary]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Resource Details</h2>
                <p className="text-xs text-slate-400 mt-0.5">Read-only view</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <span className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                  ID
                </span>
                <span className="text-sm font-mono font-semibold text-slate-800 dark:text-slate-200 select-all">
                  {selectedResource.id}
                </span>
              </div>
              <div>
                <span className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                  URI
                </span>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 select-all">
                  {selectedResource.uri}
                </span>
              </div>
              <div>
                <span className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                  Kind ID
                </span>
                <span className="inline-block text-xs bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-400 font-semibold px-2 py-1 rounded-md">
                  {selectedResource.kind_id}
                </span>
              </div>
              <div>
                <span className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                  Parent ID
                </span>
                <span className="text-sm font-mono text-slate-800 dark:text-slate-200">
                  {selectedResource.parent_id || <span className="italic text-slate-400">None</span>}
                </span>
              </div>
              <div>
                <span className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                  Status
                </span>
                <span className="inline-block text-xs font-semibold px-2 py-1 rounded-md">
                  {selectedResource.status === 1 ? (
                    <span className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20">Active</span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50">Inactive</span>
                  )}
                </span>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
              <Button onClick={() => setSelectedResource(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* --- Create Resource Kind Modal --- */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Create Resource Kind</h2>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. platform.database"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Platform Database"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Brief description of the resource kind..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white h-20 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Parent Kind
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white"
                >
                  <option value="">None (Top Level)</option>
                  {resourceKinds.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name} ({k.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Edit Resource Kind Modal --- */}
      {isEditModalOpen && selectedKind && (
        <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Edit Resource Kind</h2>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-500 dark:text-slate-400 mb-1">
                  ID (Read Only)
                </label>
                <input
                  type="text"
                  disabled
                  value={formData.id}
                  className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium text-slate-500 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Platform Database"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Brief description of the resource kind..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white h-20 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Parent Kind
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[--primary] transition-all text-slate-900 dark:text-white"
                >
                  <option value="">None (Top Level)</option>
                  {resourceKinds
                    .filter((k) => k.id !== selectedKind.id) // Cannot be own parent
                    .map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name} ({k.id})
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
